import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderIntroDots = (props: GroupOrderIntroDotsProps) => {
  const { total, active } = props;

  // ─────────────────────────────────────────────────────────────────────

  const dots = useMemo(() => {
    return Array.from({ length: total });
  }, [total]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {dots.map((_, index) => (
        <Dot key={index} index={index} active={active} />
      ))}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const Dot = (props: DotProps) => {
  const { index, active } = props;

  const isActive = index === active;

  // ─────────────────────────────────────────────────────────────────────

  const scale = useDerivedValue(
    () => withTiming(decideDotScale(active, index), { duration: theme.transitions.base }),
    [active, index],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    backgroundColor: isActive ? COLORS.active : COLORS.inactive,
  }));

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.dotWrapper}>
      <Animated.View style={[styles.dot, animatedStyle]} />
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function decideDotScale(active: number, dot: number) {
  'worklet';

  const distance = Math.abs(active - dot);

  if (dot === active) {
    return 1;
  }

  if (distance === 1) {
    return 0.75;
  }

  return 0.5;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DOT_SIZE = theme.spacing['2'];
const GAP_SIZE = theme.spacing['2'];

const COLORS = {
  active: theme.colors.WHITE,
  inactive: theme.colors.OPACITY.OATMEAL.DARKER,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: GAP_SIZE,
  },
  dotWrapper: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderIntroDotsProps = {
  total: number;
  active: number;
};

type DotProps = {
  index: number;
  active: number;
};
